import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, List, Popconfirm, Spin, Tag, Typography, message } from 'antd';
import {
  DesktopOutlined,
  LaptopOutlined,
  MobileOutlined,
  TabletOutlined,
} from '@ant-design/icons';
import api from '../../services/api';

const { Text } = Typography;

export interface UserSession {
  uid: string;
  ip: string | null;
  device: string | null;
  browser: string | null;
  signedInAt: string;
  lastSeenAt: string | null;
  /** The session doing the asking. Not offered a "Log out" of its own. */
  current: boolean;
}

const DEVICE_ICONS: Record<string, React.ReactNode> = {
  Mobile: <MobileOutlined />,
  Tablet: <TabletOutlined />,
  Mac: <LaptopOutlined />,
  Windows: <DesktopOutlined />,
  Linux: <DesktopOutlined />,
};

const deviceIcon = (device: string | null) =>
  (device && DEVICE_ICONS[device]) || <DesktopOutlined />;

/** "Mac · Chrome", skipping whichever half the user agent did not say. */
const describe = (session: UserSession): string =>
  [session.device, session.browser].filter(Boolean).join(' · ') || 'Unknown device';

/**
 * How long ago, in the roughest terms that are still useful.
 *
 * A session list is read to answer "is that me?", and a date is a worse answer
 * to that than "2 hours ago".
 */
const timeAgo = (iso: string | null): string => {
  if (!iso) return 'unknown';

  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;

  return new Date(iso).toLocaleDateString();
};

/**
 * Where this account is signed in, and how to stop it being signed in there.
 *
 * Both halves were missing. Nothing recorded a sign-in, so nobody could be
 * shown their own, and logging out cleared this browser's cookies while the
 * refresh token stayed usable for another thirty days: on a shared machine,
 * signing out did not sign you out.
 */
const SessionsSection: React.FC = () => {
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await api.get('/user/me/sessions');
      setSessions(response.data ?? []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load your sessions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const revoke = async (uid: string) => {
    setBusy(uid);
    try {
      await api.delete(`/user/me/sessions/${uid}`);
      message.success('Signed that session out.');
      await load();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to sign that session out.');
    } finally {
      setBusy(null);
    }
  };

  const revokeOthers = async () => {
    setBusy('others');
    try {
      const response = await api.delete('/user/me/sessions');
      message.success(response.data?.message || 'Signed the other sessions out.');
      await load();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to sign the other sessions out.');
    } finally {
      setBusy(null);
    }
  };

  const others = sessions.filter((session) => !session.current);

  return (
    <Card
      title="Where You're Signed In"
      bordered={false}
      style={{ marginBottom: '1rem' }}
      extra={
        others.length > 0 ? (
          <Popconfirm
            title="Sign out everywhere else?"
            description={`This signs out ${others.length} other ${
              others.length === 1 ? 'session' : 'sessions'
            }. You stay signed in here.`}
            okText="Sign them out"
            cancelText="Cancel"
            onConfirm={revokeOthers}
          >
            <Button danger size="small" loading={busy === 'others'}>
              Sign Out Everywhere Else
            </Button>
          </Popconfirm>
        ) : null
      }
    >
      {loading ? (
        <Spin tip="Loading sessions..." />
      ) : error ? (
        <Alert message="Error" description={error} type="error" showIcon />
      ) : (
        <List
          dataSource={sessions}
          locale={{ emptyText: 'No other sessions are signed in.' }}
          renderItem={(session) => (
            <List.Item
              actions={
                session.current
                  ? []
                  : [
                      <Popconfirm
                        key="revoke"
                        title="Sign this session out?"
                        description="Whoever is using it will have to sign in again."
                        okText="Sign out"
                        cancelText="Cancel"
                        onConfirm={() => revoke(session.uid)}
                      >
                        <Button danger size="small" loading={busy === session.uid}>
                          Sign Out
                        </Button>
                      </Popconfirm>,
                    ]
              }
            >
              <List.Item.Meta
                avatar={deviceIcon(session.device)}
                title={
                  <span>
                    {describe(session)}{' '}
                    {session.current && <Tag color="green">This device</Tag>}
                  </span>
                }
                description={
                  <Text type="secondary">
                    {session.ip ? `${session.ip} · ` : ''}
                    last active {timeAgo(session.lastSeenAt ?? session.signedInAt)}
                  </Text>
                }
              />
            </List.Item>
          )}
        />
      )}
    </Card>
  );
};

export default SessionsSection;
